import { api } from '../../services/authService';
import { conditionHasColumn, columnLabel, deepClone, defaultCriteriaRoot, groupSamplePct } from './criteriaUtils';

/** Shared by SegmentEditor and TemplateEditor: both build a criteria tree against org data. */

export function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

export function sanitizeName(v) {
  return String(v || '').replace(/[^a-zA-Z0-9_ -]/g, '');
}

/** Normalize stored criteria into editor state (defaults for older documents). */
export function criteriaFromSegment(raw) {
  const root = raw && raw.kind === 'group' ? deepClone(raw) : defaultCriteriaRoot();
  const walk = (n) => {
    if (!n) return;
    if (n.kind === 'condition') {
      n.value = n.value ?? '';
      n.value2 = n.value2 ?? '';
      n.folder_list = Array.isArray(n.folder_list) ? n.folder_list : [];
      n.display_name = n.display_name || '';
      n.has_value_list = Boolean(n.has_value_list);
      return;
    }
    n.children = Array.isArray(n.children) ? n.children : [];
    // Groups saved before percentage sampling keep 100% of what they match.
    n.sample_pct = groupSamplePct(n);
    n.sample_seed = Number(n.sample_seed) || 0;
    n.children.forEach(walk);
  };
  walk(root);
  return root;
}

/** Turn a saved template into a named group (own All/Any toggle and percentage slider). */
export async function templateToGroup(template, organizationId) {
  const hydrated = await hydrateCriteria(criteriaFromSegment(template.criteria), organizationId);
  return { ...hydrated, kind: 'group', display_name: template.name };
}

/**
 * Put a template group into `parent`, appended or in place of the child at `replaceIdx`.
 * Templates describe different cohorts, so a parent holding two or more named groups
 * switches to "Any of" — ANDing them would match nobody.
 */
export function withTemplateGroup(parent, group, replaceIdx = null) {
  const children = [...(parent.children || [])];
  if (replaceIdx == null) children.push(group);
  else children[replaceIdx] = group;
  const hasOtherNamed = children.some((c) => c !== group && c.kind === 'group' && c.display_name);
  return { ...parent, op: hasOtherNamed ? 'OR' : parent.op, children };
}

export function collectConditions(root, out = []) {
  if (!root) return out;
  if (root.kind === 'condition') out.push(root);
  else (root.children || []).forEach((c) => collectConditions(c, out));
  return out;
}

/** Fill display_name / has_value_list / column_type on conditions from the ES catalog. */
export async function hydrateCriteria(root, organizationId) {
  const conds = collectConditions(root).filter((c) => conditionHasColumn(c) && (!c.display_name || !c.column_type));
  if (!conds.length) return root;
  try {
    const res = await api.post('/api/v2/segments/column-resolve', {
      columns: conds.map((c) => ({ catalog: c.catalog, schema: c.schema, table: c.table, column: c.column })),
      organization_id: organizationId ? parseInt(organizationId, 10) : undefined,
    });
    const hits = Array.isArray(res.data?.hits) ? res.data.hits : [];
    const byKey = new Map(hits.map((h) => [[h.catalog, h.schema, h.table, h.column].join('.'), h]));
    const next = deepClone(root);
    collectConditions(next).forEach((c) => {
      const h = byKey.get([c.catalog, c.schema, c.table, c.column].join('.'));
      if (!h) return;
      c.display_name = c.display_name || columnLabel(h);
      c.column_type = c.column_type || h.column_type || '';
      c.has_value_list = Boolean(h.has_value_list);
      if (!c.folder_list?.length && Array.isArray(h.folder_list)) c.folder_list = h.folder_list;
    });
    return next;
  } catch (e) {
    return root;
  }
}
